"use client";

import { c as _c } from "react/compiler-runtime";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { DRAG_DROP_PASTE } from "@lexical/rich-text";
import { isMimeType, mediaFileReader } from "@lexical/utils";
import { $getNodeByKey, $insertNodes, COMMAND_PRIORITY_LOW } from "lexical";
import { useEffect, useRef } from "react";
import { $createImageNode } from "../nodes/ImageNode";
import { multiPartUpload } from "@/components/waves/create-wave/services/multiPartUpload";
import { useAuth } from "@/components/auth/Auth";
import { ACCEPTED_FILE_TYPE_LABELS, isSupportedUploadFile } from "@/services/uploads/mediaUploadMimeType";
const ACCEPTABLE_IMAGE_TYPES = ["image/", "image/heic", "image/heif", "image/gif", "image/webp"];
const INLINE_IMAGE_UPLOAD_TIMEOUT_MS = 30_000;
function isAcceptableAttachment(file: File): boolean {
  return isSupportedUploadFile(file) && !isMimeType(file, ACCEPTABLE_IMAGE_TYPES);
}
function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const controller = new AbortController();
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    timeoutId = globalThis.setTimeout(() => {
      controller.abort();
      reject(new Error(message));
    }, timeoutMs);
  });
  return Promise.race([run(controller.signal), timeoutPromise]).finally(() => {
    if (timeoutId !== undefined) {
      globalThis.clearTimeout(timeoutId);
    }
  });
}
async function uploadImage(file: File): Promise<string> {
  const multiPart = await withTimeout(signal => multiPartUpload({
    file,
    path: "drop",
    signal
  }), INLINE_IMAGE_UPLOAD_TIMEOUT_MS, "Image upload timed out. Please try again.");
  return multiPart.url;
}
export default function DragDropPaste(t0) {
  const $ = _c(7);
  const {
    onAttachmentFiles
  } = t0;
  const {
    setToast
  } = useAuth();
  const onAttachmentFilesRef = useRef(onAttachmentFiles);
  let t1;
  let t2;
  if ($[0] !== onAttachmentFiles) {
    t1 = () => {
      onAttachmentFilesRef.current = onAttachmentFiles;
    };
    t2 = [onAttachmentFiles];
    $[0] = onAttachmentFiles;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const [editor] = useLexicalComposerContext();
  let t3;
  let t4;
  if ($[3] !== editor || $[4] !== setToast) {
    t3 = () => {
      let isMounted = true;
      const unregister = editor.registerCommand(DRAG_DROP_PASTE, files => {
        (async () => {
          const filesResult = await mediaFileReader(files, [ACCEPTABLE_IMAGE_TYPES].flatMap(_temp));
          const currentOnAttachmentFiles = onAttachmentFilesRef.current;
          const attachmentFiles = currentOnAttachmentFiles ? files.filter(isAcceptableAttachment) : [];
          if (attachmentFiles.length > 0) {
            currentOnAttachmentFiles?.(attachmentFiles);
          }
          if (filesResult.length === 0 && attachmentFiles.length === 0) {
            setToast({
              message: `Unsupported file type. Accepted Types: ${ACCEPTED_FILE_TYPE_LABELS}`,
              type: "error"
            });
            return;
          }
          for (const {
            file
          } of filesResult) {
            if (isMimeType(file, ACCEPTABLE_IMAGE_TYPES)) {
              editor.update(() => {
                const imageNode = $createImageNode({
                  src: "loading"
                });
                $insertNodes([imageNode]);
                const key = imageNode.getKey();
                uploadImage(file).then(url => {
                  if (!isMounted) {
                    return;
                  }
                  editor.update(() => {
                    const node = $getNodeByKey(key);
                    if (node) {
                      node.replace($createImageNode({
                        src: url
                      }));
                    }
                  });
                }).catch(err => {
                  if (!isMounted) {
                    return;
                  }
                  editor.update(() => {
                    const node_0 = $getNodeByKey(key);
                    if (node_0) {
                      node_0.remove();
                    }
                  });
                  setToast({
                    message: err instanceof Error ? err.message : "Error uploading image. Please try again.",
                    type: "error"
                  });
                });
              });
            }
          }
        })();
        return true;
      }, COMMAND_PRIORITY_LOW);
      return () => {
        unregister();
        isMounted = false;
      };
    };
    t4 = [editor, setToast];
    $[3] = editor;
    $[4] = setToast;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  return null;
}
function _temp(x) {
  return x;
}